import { FC } from "react";
import { useSnackbar, VariantType } from "notistack";

import {
  FormTitle,
  FormWrapper,
} from "@/components/Forms/components/MonolithFormComponents";

import { DefaultStorageForm } from "./DefaultStorageForm";
import { StorageAPI } from "../storage.api";
import LoggingAPI from "@/api/logging";
import { usePermissions } from "@/hooks/usePermissions";

import {
  StorageItemForEdit,
  StorageFormData,
  EditStorageFormData,
} from "../storage.types";
import {
  processCustomAttributesForAPI,
  toStorageFormData,
} from "../storage.utils";

interface EditStorageFormProps {
  width?: number | undefined;
  defaultStorageInfo: StorageItemForEdit;
  onSubmit: (updateData: Partial<StorageItemForEdit>) => void;
  onCancel: () => void;
}

export const EditStorageForm: FC<EditStorageFormProps> = ({
  width,
  defaultStorageInfo,
  onSubmit,
  onCancel,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = usePermissions();

  const handleSubmit = async (
    _data: StorageFormData,
    diffData?: EditStorageFormData
  ) => {
    try {
      if (defaultStorageInfo.storage_id && diffData) {
        // Extract and convert custom_attributes without mutating diffData
        const { cleaned: updateData, customAttributes } =
          processCustomAttributesForAPI(diffData as Record<string, unknown>);
        if (customAttributes.length > 0) {
          updateData.custom_attributes = customAttributes;
        }

        if (Object.keys(updateData).length === 0) {
          enqueueSnackbar("No new data to update.", {
            variant: "warning" as VariantType,
          });
          onCancel();
          return;
        }

        const result = await StorageAPI.update(
          defaultStorageInfo.storage_id,
          updateData
        );
        // The API layer resolves (never rejects) on HTTP errors — treat a
        // { success: false } result as failure.
        if (result?.success === false) {
          throw new Error("Failed to update storage item");
        }

        enqueueSnackbar(
          `Storage ${defaultStorageInfo.storage_number || "#"} updated`,
          { variant: "success" as VariantType }
        );

        LoggingAPI.createLog({
          user_id: currentUser.user_id,
          message: `Edited storage item: ${
            defaultStorageInfo.storage_number || defaultStorageInfo.storage_id
          }`,
          event: "storage:edit",
          object_type: "storage",
          object_uuid: defaultStorageInfo.uuid,
        }).catch(() => {});

        // Case activity log — the legacy edit modal recorded edits of
        // case-assigned items in the case's activity log; keep that trail.
        if (defaultStorageInfo.case_id) {
          LoggingAPI.logActivity(
            defaultStorageInfo.case_id,
            currentUser.user_id,
            `Edited Storage Details ${defaultStorageInfo.storage_number || ""}`
          ).catch(() => {});
        }

        // Run the parent callback outside the API error path — the update
        // already succeeded, so a UI-side failure here must not surface as an
        // "edit failed" error or suppress the success snackbar/audit logs.
        try {
          onSubmit(updateData as Partial<StorageItemForEdit>);
        } catch {
          /* swallow — update already committed on the server */
        }
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        `An error occurred while editing storage. Please try again.`,
        { variant: "error" as VariantType }
      );
    }
  };

  return (
    <FormWrapper width={width}>
      <FormTitle>Edit Storage</FormTitle>
      <DefaultStorageForm
        onSubmit={handleSubmit}
        onCancel={onCancel}
        DefaultFormData={toStorageFormData(defaultStorageInfo)}
        caseId={defaultStorageInfo.case_id || undefined}
        includeAssignDetails={false}
        includeCustomFields={true}
        requireStorageNumber={true}
        buttonProps={{ submitText: "Save Changes" }}
      />
    </FormWrapper>
  );
};
